import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
// Aviseringsbiblioteket hämtas först när en avisering faktiskt visas.
type ToastOptions = Parameters<typeof import("sonner").toast.success>[1];
const notify =
  (kind: "info" | "error" | "success") =>
  (message: string, options?: ToastOptions) => {
    void import("sonner").then((m) => m.toast[kind](message, options));
  };
const toast = { info: notify("info"), error: notify("error"), success: notify("success") };
import {
  friendlyScanError,
  scanFailureAction,
  scanFailureDiagnostic,
  type ScanFailureDiagnostic,
  type ScanFailureStage,
} from "@/lib/scan-errors";
import { retainNativeKeepAwake } from "@/app-bridge/core/wrapper";
import {
  checkpointVideoScanFrame,
  createScanUpload,
  createScanFrameUploads,
  createVideoScanSession,
  reportVideoScanFailure,
  scanVideo,
  getJob,
  createPhotoScanUploads,
  scanPhotos,
  savePhotoScanRun,
} from "@/lib/scan-engine.lazy";
import { waitForJob } from "@/lib/job-poll";
// Bild- och videobearbetning laddas först när en skanning körs.
import type { compressVideoForScan as CompressVideo } from "@/lib/video-compress";
const extractScanFilmstrip: typeof import("@/lib/scan-filmstrip").extractScanFilmstrip = async (
  ...args
) => (await import("@/lib/scan-filmstrip")).extractScanFilmstrip(...args);
const compressVideoForScan: typeof CompressVideo = async (...args) =>
  (await import("@/lib/video-compress")).compressVideoForScan(...args);
const compressImage: typeof import("@/lib/image-compress").compressImage = async (...args) =>
  (await import("@/lib/image-compress")).compressImage(...args);
import {
  PHOTO_SCAN_MAX_IMAGES,
  type PhotoGroup,
  type PhotoScanDebug,
  type PhotoImageStatus,
} from "@/lib/photo-scan";
import { SCAN_MAX_SECONDS, type ScanDebug, type ScanItem } from "@/lib/video-scan";

export type ScanStep =
  | "idle"
  | "compress"
  | "frames"
  | "upload"
  | "scan"
  | "save"
  | "review"
  | "done";

export type ScanKind = "video" | "photos";

/** En uppladdad bild i fotoskanningen. */
export type ScanPhoto = { path: string; previewUrl: string };

export type ScanPartDebug = {
  index: number;
  startSeconds: number;
  endSeconds: number;
  bytes: number;
  resolution: string;
  compressMs: number;
  uploadMs: number | null;
  previewUrl: string;
};

export type ScanDebugInfo = {
  name: string;
  durationSeconds: number;
  originalBytes: number;
  originalType: string;
  compressMs: number | null;
  totalCompressedBytes: number | null;
  parts: ScanPartDebug[];
  scanMs: number | null;
  server: ScanDebug | null;
};

export type ScanJobState = {
  kind: ScanKind;
  step: ScanStep;
  error: string | null;
  fileName: string | null;
  items: ScanItem[] | null;
  overall: number;
  statusText: string;
  remainingSeconds: number;
  debug: ScanDebugInfo | null;
  savedRunId: string | null;
  savedRunLabel: string | null;
  /** Ökar varje gång en skanning sparats, så lyssnare kan uppdatera sina listor. */
  savedAt: number;
  /** Telefonens del är klar – resten sker på servern och appen får stängas. */
  handoff: boolean;
  /** Jobbets id i kön, så dockan kan kommas ihåg som stängd. */
  jobId: string | null;
  /** Uppladdade foton (fotoskanning), i samma ordning som AI:n numrerade dem. */
  photos: ScanPhoto[] | null;
  /** AI:ns grupper som väntar på din granskning innan de sparas. */
  groups: PhotoGroup[] | null;
  photoDebug: PhotoScanDebug | null;
  photoCoverage: PhotoImageStatus[] | null;
  sourcePreviewUrl: string | null;
  framePreviewUrls: string[];
  frameCount: number;
  /** Antal scener filmen delades upp i. */
  sceneCount: number;
  /** Exakt teknisk felorsak, endast avsedd för adminloggen. */
  failureDiagnostic: ScanFailureDiagnostic | null;
};

type ScanJobContextValue = ScanJobState & {
  busy: boolean;
  start: (file: File) => void;
  startPhotos: (files: File[]) => void;
  setGroups: (groups: PhotoGroup[]) => void;
  savePhotoGroups: (groups: PhotoGroup[], label?: string) => Promise<void>;
  reset: () => void;
};

const INITIAL: ScanJobState = {
  kind: "video",
  step: "idle",
  error: null,
  fileName: null,
  items: null,
  overall: 0,
  statusText: "Gör filmen redo…",
  remainingSeconds: 0,
  debug: null,
  savedRunId: null,
  savedRunLabel: null,
  savedAt: 0,
  handoff: false,
  jobId: null,
  photos: null,
  groups: null,
  photoDebug: null,
  photoCoverage: null,
  sourcePreviewUrl: null,
  framePreviewUrls: [],
  frameCount: 0,
  sceneCount: 0,
  failureDiagnostic: null,
};

const ScanJobContext = createContext<ScanJobContextValue | null>(null);

/** Hur stor del av det totala förloppet varje steg upptar. */
const PHASE_RANGE: Record<"compress" | "frames" | "upload" | "scan", [number, number]> = {
  frames: [0, 0.34],
  compress: [0.34, 0.5],
  upload: [0.5, 0.62],
  scan: [0.62, 1],
};

/** PUT med riktig förloppsrapportering (fetch kan inte rapportera uppladdning). */
function putWithProgress(
  url: string,
  file: File,
  onProgress: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error("Uppladdningen misslyckades. Försök igen."));
    xhr.onerror = () => reject(new Error("Uppladdningen misslyckades. Försök igen."));
    xhr.send(file);
  });
}

/** Läser filmens längd i webbläsaren så vi kan stoppa för långa klipp direkt. */
function readDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement("video");
    el.preload = "metadata";
    el.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(el.duration) ? el.duration : 0);
    };
    el.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(0);
    };
    el.src = url;
  });
}

/**
 * Kör hela videoskanningen ovanför routern, så att den fortsätter i bakgrunden
 * medan man surfar vidare på sajten. När den är klar kommer en notis med länk.
 */
export function ScanJobProvider({ children }: { children: ReactNode }) {
  const runScan = scanVideo;
  const createFrameUploads = createScanFrameUploads;
  const createVideoUpload = createScanUpload;
  const createSession = createVideoScanSession;
  const checkpointFrame = checkpointVideoScanFrame;
  const reportFailure = reportVideoScanFailure;
  const fetchJob = getJob;

  const uploadPhotos = createPhotoScanUploads;
  const runPhotoScan = scanPhotos;
  const savePhotoRun = savePhotoScanRun;

  const [state, setState] = useState<ScanJobState>(INITIAL);
  const previewUrlsRef = useRef<string[]>([]);
  const scanTimerRef = useRef<number | null>(null);
  const etaTimerRef = useRef<number | null>(null);
  const runningRef = useRef(false);

  const patch = useCallback(
    (next: Partial<ScanJobState>) => setState((prev) => ({ ...prev, ...next })),
    [],
  );

  const busy =
    state.step === "compress" ||
    state.step === "frames" ||
    state.step === "upload" ||
    state.step === "scan" ||
    state.step === "save";

  // I appen håller vi skärmen vaken medan telefonen själv laddar upp och
  // förbereder materialet. När servern tagit över får skärmen släckas igen.
  useEffect(() => {
    if (!busy || state.handoff) return;
    return retainNativeKeepAwake();
  }, [busy, state.handoff]);

  // Vi varnar bara om man verkligen stänger fliken – att byta sida är helt okej.
  useEffect(() => {
    if (!busy || state.handoff) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [busy, state.handoff]);

  useEffect(
    () => () => {
      if (scanTimerRef.current) window.clearInterval(scanTimerRef.current);
      if (etaTimerRef.current) window.clearInterval(etaTimerRef.current);
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    },
    [],
  );

  const start = useCallback(
    (input: File) => {
      if (runningRef.current) {
        toast.info("En skanning pågår redan. Vänta tills den är klar.");
        return;
      }

      const setPhase = (phase: "compress" | "frames" | "upload" | "scan", fraction: number) => {
        const [from, to] = PHASE_RANGE[phase];
        patch({ overall: from + (to - from) * Math.min(1, Math.max(0, fraction)) });
      };

      const startEta = (seconds: number) => {
        if (etaTimerRef.current) window.clearInterval(etaTimerRef.current);
        patch({ remainingSeconds: Math.max(1, Math.round(seconds)) });
        etaTimerRef.current = window.setInterval(() => {
          setState((prev) => ({
            ...prev,
            remainingSeconds: Math.max(0, prev.remainingSeconds - 1),
          }));
        }, 1000);
      };

      const stopTimers = () => {
        if (scanTimerRef.current) window.clearInterval(scanTimerRef.current);
        scanTimerRef.current = null;
        if (etaTimerRef.current) window.clearInterval(etaTimerRef.current);
        etaTimerRef.current = null;
      };

      const run = async () => {
        let failureStage: ScanFailureStage = "metadata";
        let scanSessionId: string | null = null;
        const clientStartedAt = new Date().toISOString();
        stopTimers();
        previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        previewUrlsRef.current = [];
        const sourcePreviewUrl = URL.createObjectURL(input);
        previewUrlsRef.current.push(sourcePreviewUrl);
        setState({
          ...INITIAL,
          kind: "video",
          fileName: input.name,
          sourcePreviewUrl,
          savedAt: state.savedAt,
        });

        if (!input.type.startsWith("video/")) {
          patch({ error: "Välj en videofil." });
          return;
        }
        const duration = await readDuration(input);
        if (duration > SCAN_MAX_SECONDS + 1) {
          patch({
            error: `Filmen är ${Math.round(duration)} sekunder. Max är 60 sekunder — filma ett rum i taget.`,
          });
          return;
        }

        runningRef.current = true;
        startEta(duration * 0.7 + 75);

        const info: ScanDebugInfo = {
          name: input.name,
          durationSeconds: duration,
          originalBytes: input.size,
          originalType: input.type,
          compressMs: null,
          totalCompressedBytes: null,
          parts: [],
          scanMs: null,
          server: null,
        };
        patch({ debug: { ...info }, handoff: false, jobId: null });

        try {
          // ---- 1. Bilder plockas ur ORIGINALFILMEN, före all omkodning ----
          // Det ger betydligt skarpare prylbilder än att läsa den nedskalade filmen.
          const frames: {
            path: string;
            atSecond: number;
            sceneIndex: number;
            sharpness: number;
          }[] = [];
          const { sessionId } = await createSession({
            data: { sourceFilename: input.name, durationSeconds: duration },
          });
          scanSessionId = sessionId;
          failureStage = "frames";
          patch({
            step: "frames",
            statusText: "Laddar upp film…",
            framePreviewUrls: [],
            frameCount: 0,
            sceneCount: 0,
          });
          setPhase("frames", 0);
          const pushPreview = (blob: Blob) => {
            const url = URL.createObjectURL(blob);
            previewUrlsRef.current.push(url);
            setState((prev) => ({
              ...prev,
              framePreviewUrls: [...prev.framePreviewUrls, url].slice(-12),
              frameCount: prev.frameCount + 1,
            }));
          };
          let frameIndex = 0;
          await extractScanFilmstrip(
            input,
            duration,
            (fraction, meta) => {
              setPhase("frames", fraction * 0.7);
              if (meta?.phase === "capture") {
                patch({ statusText: "Laddar upp film…", sceneCount: meta.scenes });
              } else {
                patch({ statusText: "Laddar upp film…" });
              }
            },
            async (frame) => {
              pushPreview(frame.blob);
              const { uploads } = await createFrameUploads({ data: { count: 1 } });
              const slot = uploads[0];
              if (!slot) throw new Error("Bildrutan kunde inte förberedas.");
              failureStage = "frame-upload";
              await putWithProgress(
                slot.signedUrl,
                new File([frame.blob], `scen-${frame.sceneIndex}-${frameIndex}.jpg`, {
                  type: "image/jpeg",
                }),
                (fraction) => setPhase("frames", Math.min(0.98, (frameIndex + fraction) / 48)),
              );
              const saved = {
                path: slot.path,
                atSecond: frame.atSecond,
                sceneIndex: frame.sceneIndex,
                sharpness: frame.sharpness,
              };
              frames.push(saved);
              await checkpointFrame({ data: { sessionId, frame: saved } });
              failureStage = "frames";
              frameIndex += 1;
            },
            { retainFrames: false },
          );

          patch({ step: "frames", frameCount: frames.length });
          if (!frames.length) {
            throw new Error("Inga bilder ur filmen kunde sparas. Ingen skanning har startats.");
          }

          // ---- 2. AI:n får en komprimerad film; bildrutorna används bara som skarpa resultatbilder. ----
          patch({ step: "compress", statusText: "Laddar upp film…" });
          setPhase("compress", 0);
          const compressedAt = performance.now();
          failureStage = "compress";
          let prepared: Awaited<ReturnType<typeof compressVideoForScan>> | null = null;
          try {
            prepared = await compressVideoForScan(input, (fraction) => {
              setPhase("compress", fraction);
              patch({ statusText: "Laddar upp film…" });
            });
          } catch (compressionError) {
            const diagnostic = scanFailureDiagnostic(compressionError, "compress", input);
            patch({ failureDiagnostic: diagnostic, statusText: "Fortsätter med filmens bildrutor…" });
            void reportFailure({ data: { sessionId, diagnostic, fatal: false } }).catch(() => undefined);
          }
          info.compressMs = Math.round(performance.now() - compressedAt);
          info.totalCompressedBytes = prepared?.segments.reduce((sum, segment) => sum + segment.bytes, 0) ?? 0;

          patch({ step: "upload", statusText: "Laddar upp film…" });
          const parts: { path: string; startSeconds: number }[] = [];
          const preparedVideo = prepared;
          for (let index = 0; index < (preparedVideo?.segments.length ?? 0); index += 1) {
            failureStage = "video-upload";
            const segment = preparedVideo?.segments[index];
            if (!segment) continue;
            const upload = await createVideoUpload({
              data: { mimeType: segment.file.type || preparedVideo?.mimeType || "video/mp4", sizeBytes: segment.file.size },
            });
            const uploadStarted = performance.now();
            await putWithProgress(upload.signedUrl, segment.file, (fraction) => {
              setPhase("upload", (index + fraction) / (preparedVideo?.segments.length || 1));
            });
            parts.push({ path: upload.path, startSeconds: segment.startSeconds });
            const previewUrl = URL.createObjectURL(segment.file);
            previewUrlsRef.current.push(previewUrl);
            info.parts.push({
              index,
              startSeconds: segment.startSeconds,
              endSeconds: segment.endSeconds,
              bytes: segment.bytes,
              resolution: `${segment.width}×${segment.height}`,
              compressMs: segment.elapsedMs,
              uploadMs: Math.round(performance.now() - uploadStarted),
              previewUrl,
            });
          }
          if (!parts.length && !frames.length) throw new Error("Filmen kunde inte laddas upp för analys.");

          patch({ step: "scan", statusText: "AI:n går igenom filmen…" });
          failureStage = "queue";
          const scanStarted = performance.now();
          setPhase("scan", 0);
          const { jobId } = await runScan({
            data: {
              parts,
              frames,
              sourceFilename: input.name,
              durationSeconds: duration,
              sessionId,
               clientDebug: {
                 startedAt: clientStartedAt,
                 uploadCompletedAt: new Date().toISOString(),
                 originalBytes: input.size,
                 originalType: input.type,
                 compressMs: info.compressMs,
                 totalCompressedBytes: info.totalCompressedBytes,
                 parts: info.parts.map(({ previewUrl: _previewUrl, ...part }) => part),
                 frames: frames.map(({ path: _path, ...frame }) => frame),
               },
            },
          });
          // Härifrån sköter servern allt – appen får stängas.
          patch({
            jobId,
            handoff: true,
            statusText: "Vi jobbar vidare i bakgrunden…",
            remainingSeconds: 0,
          });
          const job = await waitForJob(
            (id) => fetchJob({ data: { id } }),
            jobId,
            (status, current) =>
              patch({
                statusText:
                  status === "queued"
                    ? "Din videoskanning väntar på sin tur…"
                    : status === "running"
                      ? (current.progress?.step ?? "AI:n identifierar prylar i filmen…")
                      : "Klar.",
              }),
          );
          failureStage = "analysis";
          if (job.status !== "done") {
            throw new Error(job.error || "Skanningen misslyckades. Försök igen.");
          }
          const result = (job.result ?? {}) as {
            items: ScanItem[];
            runId: string;
            label: string;
            savedItems: { id: string; atSecond: number | null }[];
            debug?: ScanDebug | null;
          };
          if (scanTimerRef.current) window.clearInterval(scanTimerRef.current);
          scanTimerRef.current = null;
          info.scanMs = Math.round(performance.now() - scanStarted);
          info.server = result.debug ?? null;
          setPhase("scan", 1);
          patch({
            debug: { ...info },
            items: result.items,
            step: "save",
            statusText: "Sparar dina prylar…",
          });

          const label: string | null = result.label ?? null;
          patch({ savedRunId: result.runId, savedRunLabel: result.label });

          stopTimers();
          runningRef.current = false;
          setState((prev) => ({
            ...prev,
            step: "done",
            remainingSeconds: 0,
            overall: 1,
            savedAt: Date.now(),
          }));

          const count = result.items.length;
          if (!count) toast.info("Videoskanningen är klar, men vi hittade inga säljbara prylar.");
        } catch (err) {
          stopTimers();
          runningRef.current = false;
          const diagnostic = scanFailureDiagnostic(err, failureStage, input);
          if (scanSessionId) {
            void reportFailure({ data: { sessionId: scanSessionId, diagnostic, fatal: true } }).catch(() => undefined);
          }
          patch({
            step: "idle",
            error: scanFailureAction(diagnostic),
            failureDiagnostic: diagnostic,
          });
          toast.error("Skanningen misslyckades. Försök igen.");
        }
      };

      void run();
    },
    [
      checkpointFrame,
      createFrameUploads,
      createSession,
      createVideoUpload,
      fetchJob,
      patch,
      reportFailure,
      runScan,
      state.savedAt,
    ],
  );

  const startPhotos = useCallback(
    (files: File[]) => {
      if (runningRef.current) {
        toast.info("En skanning pågår redan. Vänta tills den är klar.");
        return;
      }

      const run = async () => {
        if (scanTimerRef.current) window.clearInterval(scanTimerRef.current);
        scanTimerRef.current = null;
        if (etaTimerRef.current) window.clearInterval(etaTimerRef.current);
        etaTimerRef.current = null;
        previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        previewUrlsRef.current = [];

        const picked = files.filter((f) => f.type.startsWith("image/"));
        setState({
          ...INITIAL,
          kind: "photos",
          savedAt: state.savedAt,
          fileName: `${picked.length} foton`,
        });

        if (!picked.length) {
          patch({ error: "Välj bilder på dina prylar." });
          return;
        }
        if (picked.length > PHOTO_SCAN_MAX_IMAGES) {
          patch({
            error: `Du valde ${picked.length} bilder. Max är ${PHOTO_SCAN_MAX_IMAGES} per omgång.`,
          });
          return;
        }

        runningRef.current = true;
        patch({
          step: "compress",
          statusText: "Gör bilderna redo…",
          handoff: false,
          jobId: null,
          remainingSeconds: Math.max(20, picked.length * 4),
        });
        etaTimerRef.current = window.setInterval(() => {
          setState((prev) => ({
            ...prev,
            remainingSeconds: Math.max(1, prev.remainingSeconds - 1),
          }));
        }, 1000);

        try {
          const prepared: Blob[] = [];
          for (let i = 0; i < picked.length; i += 1) {
            prepared.push(await compressImage(picked[i]!, 1400, 0.82));
            patch({
              overall: 0.25 * ((i + 1) / picked.length),
              statusText: `Gör bilderna redo… ${i + 1}/${picked.length}`,
            });
          }

          patch({ step: "upload", statusText: "Laddar upp bilderna…" });
          const { uploads } = await uploadPhotos({
            data: {
              files: prepared.map((blob) => ({
                mimeType: blob.type || "image/jpeg",
                sizeBytes: blob.size,
              })),
            },
          });

          const photos: ScanPhoto[] = [];
          for (let i = 0; i < uploads.length; i += 1) {
            const upload = uploads[i]!;
            const blob = prepared[upload.index]!;
            await putWithProgress(
              upload.signedUrl,
              new File([blob], `foto-${i}.jpg`, { type: blob.type || "image/jpeg" }),
              (p) => patch({ overall: 0.25 + 0.3 * ((i + p) / uploads.length) }),
            );
            const previewUrl = URL.createObjectURL(blob);
            previewUrlsRef.current.push(previewUrl);
            photos.push({ path: upload.path, previewUrl });
          }
          patch({ photos, statusText: "Vi går igenom bilderna…", step: "scan" });
          patch({
            photoCoverage: photos.map((_, imageIndex) => ({
              imageIndex,
              status: "pending" as const,
              itemNames: [],
            })),
          });

          let crept = 0.55;
          scanTimerRef.current = window.setInterval(() => {
            crept = Math.min(0.95, crept + 0.01);
            patch({ overall: crept });
          }, 700);

          const { jobId: photoJobId } = await runPhotoScan({
            data: { paths: photos.map((p) => p.path) },
          });
          patch({ jobId: photoJobId, handoff: true, statusText: "Din skanning ligger i kö…" });
          const photoJob = await waitForJob(
            (id) => fetchJob({ data: { id } }),
            photoJobId,
            (status, current) =>
              patch({
                statusText:
                  status === "queued"
                    ? "Din skanning ligger i kö…"
                    : (current.progress?.step ?? "Vi går igenom bilderna…"),
                ...(current.progress?.coverage ? { photoCoverage: current.progress.coverage } : {}),
              }),
          );
          if (photoJob.status !== "done") {
            throw new Error(photoJob.error || "Skanningen misslyckades. Försök igen.");
          }
          const result = (photoJob.result ?? {}) as {
            groups: PhotoGroup[];
            coverage?: PhotoImageStatus[];
            debug?: PhotoScanDebug | null;
          };
          if (scanTimerRef.current) window.clearInterval(scanTimerRef.current);
          scanTimerRef.current = null;
          if (etaTimerRef.current) window.clearInterval(etaTimerRef.current);
          etaTimerRef.current = null;
          runningRef.current = false;

          const groups = result.groups;
          setState((prev) => ({
            ...prev,
            step: "review",
            overall: 1,
            remainingSeconds: 0,
            groups,
            photoCoverage: result.coverage ?? null,
            photoDebug: result.debug ?? null,
            statusText: "Klart – granska grupperna innan vi sparar.",
          }));

          toast.success(
            groups.length
              ? `Bilderna är genomgångna — ${groups.length} ${groups.length === 1 ? "pryl" : "prylar"} att granska.`
              : "Bilderna är genomgångna, men vi hittade inga säljbara prylar.",
            {
              duration: 12000,
              action: {
                label: "Granska",
                onClick: () => {
                  window.location.href = "/mina-prylar";
                },
              },
            },
          );
        } catch (err) {
          if (scanTimerRef.current) window.clearInterval(scanTimerRef.current);
          scanTimerRef.current = null;
          if (etaTimerRef.current) window.clearInterval(etaTimerRef.current);
          etaTimerRef.current = null;
          runningRef.current = false;
          patch({
            step: "idle",
            error: friendlyScanError(err),
          });
          toast.error("Fotoskanningen misslyckades. Försök igen.");
        }
      };

      void run();
    },
    [fetchJob, patch, runPhotoScan, state.savedAt, uploadPhotos],
  );

  const setGroups = useCallback((groups: PhotoGroup[]) => patch({ groups }), [patch]);

  const savePhotoGroups = useCallback(
    async (groups: PhotoGroup[], label?: string) => {
      const photos = state.photos ?? [];
      if (!photos.length) return;
      patch({ step: "save", statusText: "Sparar dina prylar…" });
      try {
        const saved = await savePhotoRun({
          data: {
            ...(label ? { label } : {}),
            photoCount: photos.length,
            items: groups
              .filter((group) => group.imageIndexes.length > 0)
              .map((group) => ({
                name: group.name,
                descriptor: group.descriptor ?? "",
                note: group.note ?? "",
                category: group.category ?? null,
                quantity: group.quantity ?? 1,
                valueLow: Math.max(0, Math.round(group.valueLow)),
                valueHigh: Math.max(0, Math.round(group.valueHigh)),
                confidence: group.confidence,
                imagePaths: group.imageIndexes
                  .map((index) => photos[index]?.path)
                  .filter((path): path is string => !!path),
              }))
              .filter((item) => item.imagePaths.length > 0),
            unusedPaths: photos.map((p) => p.path),
          },
        });
        setState((prev) => ({
          ...prev,
          step: "done",
          groups: null,
          savedRunId: saved.runId,
          savedRunLabel: saved.label,
          savedAt: Date.now(),
          statusText: "Sparat.",
        }));
        toast.success(`Sparat — ${saved.itemCount} prylar i ”${saved.label}”.`);
      } catch (err) {
        patch({
          step: "review",
          error: friendlyScanError(err),
        });
        toast.error("Kunde inte spara prylarna. Försök igen.");
      }
    },
    [patch, savePhotoRun, state.photos],
  );

  const reset = useCallback(() => {
    if (runningRef.current) return;
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrlsRef.current = [];
    setState((prev) => ({ ...INITIAL, savedAt: prev.savedAt }));
  }, []);

  const value = useMemo<ScanJobContextValue>(
    () => ({ ...state, busy, start, startPhotos, setGroups, savePhotoGroups, reset }),
    [state, busy, start, startPhotos, setGroups, savePhotoGroups, reset],
  );

  return <ScanJobContext.Provider value={value}>{children}</ScanJobContext.Provider>;
}

export function useScanJobOptional(): ScanJobContextValue | null {
  return useContext(ScanJobContext);
}

export function useScanJob(): ScanJobContextValue {
  const ctx = useContext(ScanJobContext);
  if (!ctx) throw new Error("useScanJob måste användas inuti ScanJobProvider");
  return ctx;
}
